import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Confirm, Message } from 'semantic-ui-react';

import { graphql } from 'react-apollo';
import withPagination from './WithPagination';
import Search from '../views/DealerManager/Search';
import List from '../views/DealerManager/List';
import dealerListQuery from '../graphql/dealerListQuery';
import { setSearch } from '../data/searchField/actions';
import { setPage as setPageAction } from '../data/page/actions';
import AssignForm from '../views/DealerManager/AssignForm';
import unassignGroupDealerMutation from '../graphql/unassignGroupDealerMutation';
import assignGroupDealerMutation from '../graphql/assignGroupDealerMutation';
import { DealerInfoAction } from '../views/DealerManager/ListRow';
import DealerInformationForm from '../views/DealerInformation/DealerInformationForm';
import createOrUpdateDealerInformationMutation from '../graphql/createDealerInformationMutation';

export const ResponseMessage = {
  Assign: {
    READY: 'Successfully assigned dealer to group',
    FAILED: 'Failed to assign dealer to group',
  },
  Unassign: {
    READY: 'Successfully unassigned dealer from group',
    FAILED: 'Failed to unassign dealer from group',
  },
};

export const AssignDealerStatus = {
  READY: 'READY',
  FAILED: 'FAILED',
  WARNING: 'warning',
};

const getDealerActionInput = (values, action) => {
  const {
    customerNumber,
    customerName,
    address1,
    address2,
    customerCity,
    customerState,
    customerZip,
    customerPhone,
    customerEmail,
    customerType,
    isRep,
    id,
  } = values;

  const dealerInformation = {
    customerNumber,
    customerName,
    address1,
    address2,
    customerCity,
    customerState,
    customerZip,
    customerPhone,
    customerEmail,
    customerType,
    isRep,
  };

  if (action === DealerInfoAction.HANDLE_EDIT && id) {
    dealerInformation.id = id;
  }

  return { dealerInformation };
};

const DealerManager = props => {
  const {
    data,
    searchFields,
    setSearchFields,
    setPage,
    assignMutation,
    unassignMutation,
    createOrUpdateDealer,
  } = props;

  const { customerDealerList, refetch } = data;

  const [isDealer, setIsDealer] = useState(true);
  const [dealerError, setDealerError] = useState('');
  const [handleAssign, setHandleAssign] = useState(false);
  const [handleUnassign, setHandleUnassign] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [assignActionMsg, setAssignActionMsg] = useState('');
  const [assignActionSuccess, setAssignActionSuccess] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const dealerSearchQuery = event => {
    const { dealerNumber } = event.target;
    if (dealerNumber.value && !/^5[0-9]{6,6}$/i.test(dealerNumber.value)) {
      setIsDealer(false);
      setDealerError('Must be a valid Dealer Number');
    } else {
      setIsDealer(true);
      setDealerError('');
    }
    const variableArguments = {
      dealer_no: dealerNumber.value,
    };
    refetch({
      ...variableArguments,
    });

    setSearchFields(variableArguments);
    setPage();
  };

  const onSelectDealerAction = (dealer, action) => {
    switch (action) {
      case DealerInfoAction.HANDLE_ASSIGN:
        setHandleAssign(true);
        setSelectedDealer(dealer);
        break;
      case DealerInfoAction.HANDLE_UNASSIGN:
        setHandleUnassign(true);
        setSelectedDealer(dealer);
        break;
      case DealerInfoAction.HANDLE_EDIT:
        setIsEditModalOpen(true);
        setEditingDealer(dealer);
        break;
      default:
        setSelectedDealer(dealer);
        break;
    }
  };

  const onSuccessSubmit = (msg, actionStatus) => {
    setAssignActionSuccess(actionStatus);
    setAssignActionMsg(msg);
    setHandleAssign(false);
    setHandleUnassign(false);
    setSelectedDealer(null);
  };

  const handleError = error => {
    setAssignActionSuccess(null);
    setAssignActionMsg(Array.isArray(error) ? error : error.message);
    setHandleUnassign(false);
    setHandleAssign(false);
    setSelectedDealer(null);
  };

  const onExcuteMutationDealer = async(mutation, mutationName, action, payload) => {
    const response = await mutation({
      variables: { ...payload },
    });
    if (response && response.data) {
      const { data: mutationData, message } = response.data[mutationName];
      if (mutationData) {
        let isSuccess = false;
        let msg = '';
        if (
          [
            DealerInfoAction.HANDLE_ASSIGN,
            DealerInfoAction.HANDLE_UNASSIGN,
          ].includes(action)
        ) {
          const recordStatus = record => (record.status === AssignDealerStatus.READY
            && (record.isImported || record.isRemoved)
            ? AssignDealerStatus.READY
            : AssignDealerStatus.FAILED);
          const status = recordStatus(mutationData[0]);
          isSuccess = recordStatus(mutationData[0]) === AssignDealerStatus.READY;
          msg = ResponseMessage[action][status];
        }
        onSuccessSubmit(msg, isSuccess);
      } else {
        handleError({ message });
      }
      refetch();
    }
  };

  const onHandleSubmitDealer = async(groupId, action) => {
    if (!selectedDealer) return;

    const input = {
      groupId: groupId || selectedDealer.groupId,
      dealerNumber: [Number(selectedDealer.customerNumber)],
    };
    try {
      switch (action) {
        case DealerInfoAction.HANDLE_ASSIGN:
          onExcuteMutationDealer(
            assignMutation,
            'assignedGroupDealer',
            action,
            input
          );
          break;

        case DealerInfoAction.HANDLE_UNASSIGN:
          onExcuteMutationDealer(
            unassignMutation,
            'unassignedGroupDealer',
            action,
            input
          );
          break;

        default:
          // No action for other types
          break;
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleCloseUnassign = () => {
    setHandleUnassign(false);
  };

  const onCloseAssignForm = () => {
    setHandleAssign(false);
  };

  const handleMessageDismiss = () => {
    setAssignActionMsg(false);
  };

  const handleCreateNewDealer = () => {
    setIsNewModalOpen(true);
  };

  const onCloseNewForm = () => {
    setIsNewModalOpen(false);
    setIsEditModalOpen(false);
    setEditingDealer(null);
    setFormErrorMessage('');
    setAssignActionMsg('');
    setAssignActionSuccess(false);
  };

  const onClearFormError = () => {
    setFormErrorMessage('');
  };

  const onFailedSubmit = (error, msg) => {
    setIsFormLoading(false);
    const errorMessage = msg !== undefined
      ? msg
      : Array.isArray(error)
        ? error.join(', ')
        : error.message || 'Network error occurred';

    // Show error in modal, not as flash message
    setFormErrorMessage(errorMessage);
  };

  const onExcuteMutationDealerInfo = async(
    mutation,
    mutationResultName,
    values,
    action
  ) => {
    try {
      const input = getDealerActionInput(values, action);
      const response = await mutation({
        variables: { ...input },
      });

      const { success, errors, msg } = response.data[mutationResultName];
      let assignActionMessage = '';
      if (msg === 'Dealer Information updated successfully.') {
        assignActionMessage = 'Successfully updated Dealer Information.';
      } else if (msg === 'Dealer Information created successfully.') {
        assignActionMessage = 'Successfully created new Dealer Information.';
      }
      if (success) {
        const isEdit = action === DealerInfoAction.HANDLE_EDIT;
        setIsNewModalOpen(false);
        setIsEditModalOpen(false);
        setEditingDealer(null);
        setFormErrorMessage('');
        setIsFormLoading(false);
        setAssignActionSuccess(true);
        setAssignActionMsg(isEdit
          ? 'Successfully updated Dealer Information.'
          : assignActionMessage);

        // Refetch data
        if (refetch) {
          refetch();
        }
      } else {
        onFailedSubmit(errors, msg);
      }
    } catch (error) {
      onFailedSubmit(error, undefined);
    }
  };

  const onFormSubmitHandler = async formData => {
    try {
      setIsFormLoading(true);
      setFormErrorMessage('');

      const { action } = formData;

      switch (action) {
        case DealerInfoAction.HANDLE_CREATE:
        case DealerInfoAction.HANDLE_EDIT:
          await onExcuteMutationDealerInfo(
            createOrUpdateDealer,
            'createOrUpdateDealerInformation',
            formData,
            action
          );
          break;
        default:
          break;
      }
    } catch (error) {
      onFailedSubmit(error, undefined);
    }
  };

  return (
    <div>
      {assignActionMsg && (
        <Message
          onDismiss={handleMessageDismiss}
          content={assignActionMsg}
          positive
          negative={!assignActionSuccess}
        />
      )}
      <Button color='green' onClick={handleCreateNewDealer} inverted>
        Create Dealer Information
      </Button>
      <br />
      <br />
      <Search
        onSubmit={dealerSearchQuery}
        searchFields={searchFields}
        searchOptions={['dealerNumber']}
      />
      {!isDealer && <span style={{ color: 'red' }}>{dealerError}</span>}
      <br />
      <br />
      <DealerInformationForm
        isModalOpen={isNewModalOpen}
        title='Create new Dealer Information'
        onCloseHandler={onCloseNewForm}
        onSubmitHandler={onFormSubmitHandler}
        isLoading={isFormLoading}
        errorMessage={formErrorMessage}
        onClearError={onClearFormError}
      />
      <DealerInformationForm
        isModalOpen={isEditModalOpen}
        title='Edit Dealer Information'
        onCloseHandler={onCloseNewForm}
        onSubmitHandler={onFormSubmitHandler}
        isLoading={isFormLoading}
        errorMessage={formErrorMessage}
        onClearError={onClearFormError}
        initialData={editingDealer}
        isEdit
      />
      <List
        dealerList={customerDealerList}
        onSelectDealer={onSelectDealerAction}
      />
      {selectedDealer && (
        <AssignForm
          openForm={handleAssign}
          onCloseForm={onCloseAssignForm}
          submitForm={onHandleSubmitDealer}
        />
      )}
      {selectedDealer && (
        <Confirm
          open={handleUnassign}
          content={`Are you sure you want to unassign this dealer from group ${selectedDealer.groupName}?`}
          onCancel={handleCloseUnassign}
          onConfirm={() => onHandleSubmitDealer('', DealerInfoAction.HANDLE_UNASSIGN)}
        />
      )}
    </div>
  );
};

DealerManager.propTypes = {
  data: PropTypes.shape({
    customerDealerList: PropTypes.array,
    refetch: PropTypes.func,
  }).isRequired,
  searchFields: PropTypes.object,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  assignMutation: PropTypes.func.isRequired,
  unassignMutation: PropTypes.func.isRequired,
  createOrUpdateDealer: PropTypes.func.isRequired,
};

DealerManager.defaultProps = {
  searchFields: {},
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPageAction());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return {
    searchFields,
  };
};

export default compose(
  withRouter,
  withPagination(dealerListQuery, 'customerDealerList'),
  connect(mapStateToProps, mapDispatchToProps),
  graphql(assignGroupDealerMutation, { name: 'assignMutation' }),
  graphql(unassignGroupDealerMutation, { name: 'unassignMutation' }),
  graphql(createOrUpdateDealerInformationMutation, {
    name: 'createOrUpdateDealer',
  })
)(DealerManager);
